import { DefaultPrivacyLevel, type RumInitConfiguration, type Site } from '@datadog/browser-rum'
import Config from 'APP/Config/index'
import PackageJson from '../../../package.json'
import type { LogsInitConfiguration } from '@datadog/browser-logs'

type RumCredentials = Pick<RumInitConfiguration, 'applicationId' | 'clientToken'>

const usRumConfig: RumCredentials = {
  applicationId: Config.DD_US_APPLICATION_ID as string,
  clientToken: Config.DD_US_CLIENT_TOKEN as string,
}

const datadogSite: Site = 'datadoghq.com'
const datadogLogsClientToken: string = Config.DD_US_LOGS_CLIENT_TOKEN as string
const datadogRumCredentials: RumCredentials = usRumConfig
const sessionReplaySampleRate: number = Config.DD_RUM_SESSION_REPLAY_SAMPLE_RATE

// Configuration shared between RUM and Browser Logs
type TBaseConfigKeys =
  | 'env'
  | 'service'
  | 'sessionSampleRate'
  | 'site'
  | 'telemetrySampleRate'
  | 'useSecureSessionCookie'
  | 'version'

const baseConfig = {
  env: (Config.ENVIRONMENT as string).toLowerCase(),
  service: 'member-app',
  sessionSampleRate: 100, // Enable session sampling for 100% of sessions
  site: datadogSite,
  telemetrySampleRate: 0,
  useSecureSessionCookie: true,
  version: PackageJson.version,
} satisfies Pick<LogsInitConfiguration, TBaseConfigKeys> &
  Pick<RumInitConfiguration, TBaseConfigKeys>

// Browser Logs configuration
// This applies to Web only, for mobile logs are managed via RUM.
export const browserLogsConfig = {
  ...baseConfig,
  clientToken: datadogLogsClientToken,
  trackingConsent: 'granted',
  forwardErrorsToLogs: false,
  forwardConsoleLogs: [], // console logs are too noisy
  forwardReports: ['csp_violation'],
} satisfies LogsInitConfiguration

// RUM configuration
export const rumConfig = {
  defaultPrivacyLevel: DefaultPrivacyLevel.MASK,
  sessionReplaySampleRate, // Sample rate configurable via DD_RUM_SESSION_REPLAY_SAMPLE_RATE, defaults to 1%
  traceSampleRate: 100, // Enable tracing for all requests
  trackResources: true,
  trackLongTasks: true,
  trackUserInteractions: true,
  enablePrivacyForActionName: true,
  allowedTracingUrls: [/^(?!.*auth).*dialoguecorp/],
  ...datadogRumCredentials,
  ...baseConfig,
} satisfies RumInitConfiguration

export const globalContextConfig = {
  product_name: Config.PRODUCT_NAME,
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export type UserInfo = {
  id: string
  name?: string
  email?: string
  extraInfo?: Record<string, unknown>
}
